import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../providers/preferenceProvider';
import BookingOverlay from '../shared/overlays/editBooking';
import RoomOverlay from '../shared/overlays/editRoom';
import '../css/book.css';
import { getRoomsWithAvailability } from '../api/room-api';
import { getFiltersMakeBookingPage } from '../api/filter-api'
import RoomList from '../shared/rooms/roomList';
import DatePicker from '../shared/datePicker'
import FiltersList from '../shared/filters/filtersList'
import { useConfig } from '../providers/configProvider';
import SearchComponent from '../shared/searchComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning, faCheckCircle, faTimesCircle, faChevronDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { createBooking } from '../api/booking-api';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACL } from '../acls';

const Bookings = () => {
  const location = useLocation();
  const navigate = useNavigate();
 
  const attempted = useRef(false);
  const { config, isAuthorized } = useConfig();

  const userId = config.user.id;
  const { t } = useLanguage();
  const [rooms, setRooms] = useState([]);

  // Overlays
  const [showBookingOverlay, setShowBookingOverlay] = useState(false);
  const [overlayBooking, setOverlayBooking] = useState({});
  const [showRoomOverlay, setShowRoomOverlay] = useState(false);
  const [overlayRoom, setOverlayRoom] = useState(null);

  // Filtering
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedList, setSelectedList] = useState({});
  const [filtersList, setFiltersList] = useState([]);
  const [search, setSearch] = useState('');
  const [fromTime, setFromTime] = useState();
  const [toTime, setToTime] = useState();
  const [availabilityDate, setAvailabilityDate] = useState();
  const [clear, setClear] = useState(0);

  const { refresh } = useConfig();

  // Loading states
  const [isQuickBooking, setIsQuickBooking] = useState(false)
  const [bookingStatus, setBookingStatus] = useState("")
  const [bookingMessage, setBookingMessage] = useState("")

  // Open/close filters - only mobile
  const [filterToggle, setFilterToggle] = useState( window.innerWidth <= 768 ? false : true );

  useEffect(() => {
    async function checkRoomAvailability(roomId, startTime, endTime) {
      const queryParams = new URLSearchParams()
      const startDateObj = new Date(startTime);
      const endDateObj = new Date(endTime);
      queryParams.append('startTime', startDateObj.toISOString());
      queryParams.append('endTime', endDateObj.toISOString());

      try {
        const response = await getRoomsWithAvailability(queryParams)
        const room = response.data.find((r) => r.id === Number.parseInt(roomId))
  
        if (!room) {
          return { available: false, reason: "Room is not available" }
        }
        // overlap in bookigns
        const hasConflict = room.Bookings.some((booking) => {
          const bookingStart = new Date(booking.startTime)
          const bookingEnd = new Date(booking.endTime)
          return bookingStart < endTime && bookingEnd > startTime
        })
  
        if (hasConflict) {
          return { available: false, reason: "Room is already booked for this time slot" }
        }
  
        return { available: true, room: room }
      } catch (error) {
        return { available: false, reason: "Error checking availability" }
      }
    }

    const attemptToBook = async () => {
      if (attempted.current) return;
      attempted.current = true;
  
      const query = new URLSearchParams(location.search);
      const roomId = query.get('roomId');
      const start = query.get('start');
      const end = query.get('end');
  
      if (!roomId || !start || !end) return;

      setIsQuickBooking(true)
      setBookingStatus("loading")
      setBookingMessage("Checking availability and booking room...")

      const startDateObj = new Date(start);
      const endDateObj = new Date(end);
      const bookingKey = `booking-${roomId}-${start}-${end}`;
  
      if (localStorage.getItem(bookingKey)) {
        setBookingStatus('error')
        setBookingMessage('Booking already attempted')
        setTimeout(() => {
          setIsQuickBooking(false)
        }, 2000)
        return
      }
  
      localStorage.setItem(bookingKey, 'attempted');
      // const availabilityCheck = await checkRoomAvailability(roomId, start, end);
  
      // if (!availabilityCheck.available) {
        // setBookingStatus('error')
        // setBookingMessage('Room is not available for the selected time')
        // setTimeout(() => {
          // setIsQuickBooking(false)
          // localStorage.removeItem(bookingKey)
          // navigate(location.pathname, { replace: true })
        // }, 2000)
        // return
      // }
  
      const roomTitle = 'Quick booked room with display';
  
      const bookingPayload = {
        title: roomTitle,
        description: 'This meeting was created with the quick booking option',
        roomId: roomId,
        startTime: new Date(startDateObj).toISOString(), 
        endTime: new Date(endDateObj).toISOString(),
        userList: [userId],
      };
  
      try {
        await createBooking(userId, bookingPayload);
        localStorage.removeItem(bookingKey);
        refresh();

        setBookingStatus('success')
        setBookingMessage('Booking successful! Redirecting...')
  
        setTimeout(() => {
          navigate(`/bookings?search=Quick+booked+room+with+display`, { replace: true })
          setIsQuickBooking(false)
        }, 2000)
      } catch (error) {
        setBookingStatus('error')
        setBookingMessage('Failed to create booking. Please try again.')
        setTimeout(() => {
          setIsQuickBooking(false)
          localStorage.removeItem(bookingKey)
          navigate(location.pathname, { replace: true })
        }, 2000)
      }
    };
  
    attemptToBook();
  }, [location.search, userId, refresh, navigate]);
  

  // Refreshes room list
  useEffect(() => {
    const timeout = setTimeout(() => {
      getRooms(selectedList);
    }, 100);
    
    return () => clearTimeout(timeout);
  }, [selectedDate, selectedList, search, fromTime, toTime]
  )

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await getFiltersMakeBookingPage();
        const filters = response?.data;
        setFiltersList(filters);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };

    fetchFilters();
  }, []);

  function getRooms(filters) {
    const queryParams = new URLSearchParams();
    fetchData(selectedDate, queryParams, setRooms, filters);
  }

  function fetchData(selectedDate, queryParams, setRooms, filters = undefined) {
    queryParams.append('date', new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())).toISOString());
    queryParams.append('search', search);
    queryParams.append('startTime', fromTime?.toISOString());
    queryParams.append('endTime', toTime?.toISOString());
    
    if (filters){
      getQueryParamsFromSelected(filters, queryParams);
    }

    getRoomsWithAvailability(queryParams)
      .then((response) => {
        // Only if there has been a change
        if (JSON.stringify(response.data) !== JSON.stringify(rooms)){
          setRooms(response.data);
        }
      })
      .catch((error) => {
        console.error('Error fetching rooms:', error);
      });
  }

  function getQueryParamsFromSelected(filters, queryParams) {
    for (const [name, items] of Object.entries(filters)) {
      items.forEach(item => {
        queryParams.append(name, item.key.toString());
      });
    }
  }

  function isBookingOwner(booking) {
    if (booking && booking?.Users) {
      return booking.Users.some(user => user.id === config.user.id && user.UserBooking.creator);
    }
  }

  function selectedListChanged(filters){
    setSelectedList(filters);
  }

  function handleSearch(search) {
    setSearch(search)
  }

  function handleFromChange(event) {
    if (!event.target.value){
      setFromTime(undefined)
      return;
    };

    const [hours, minutes] = event.target.value.split(':');
    const newFromTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), hours, minutes);
    setFromTime(newFromTime);
    if (fromTime && toTime && toTime.getTime() - newFromTime.getTime()) {
      setSelected(newFromTime, toTime);
    }
  }

  function handleToChange(event) {
    if (!event.target.value){
      setToTime(undefined)
      return;
    };
    
    const [hours, minutes] = event.target.value.split(':');
    const newToTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), hours, minutes);
    setToTime(newToTime);
    if (fromTime && newToTime && newToTime.getTime() - fromTime.getTime() > 0) {
      setSelected(fromTime, newToTime);
    }
  }

  function setSelected(from, to) {
    setAvailabilityDate([from, to]);
  }

  function openBookingOverlay(createBooking) {
    setOverlayBooking(createBooking);
    setShowBookingOverlay(true);
  }

  function closeBookingOverlay() {
    setShowBookingOverlay(false);
    getRooms();
    refresh(); // refresh config, updates credits
  }

  function openRoomOverlay(room) {
    setOverlayRoom(room);
    setShowRoomOverlay(true);
  }
  function closeRoomOverlay() {
    setShowRoomOverlay(false);
    setOverlayRoom(null);
  }

  function clearFilters() {
    setSearch('');
    setSelectedDate(new Date());
    setSelectedList({});
    setFromTime(undefined);
    setToTime(undefined);

    setClear(prev => prev + 1);
  }

  return (
    <>
      <main className="min-h-full main-container">
        <div className="book-container">
          {/* Filters */} 
          <div className='book-filter-box md:w-[19.5rem] items-center overflow-visible'>
            <div className={`main-header max-md:flex max-md:flex-row max-md:justify-between max-md:items-center ${!filterToggle ? "rounded-bl-[6px] rounded-br-[6px]" : ""}`}
              onClick={() => setFilterToggle(!filterToggle || window.innerWidth > 768)}>
                {t.filters}
              <div className={`mr-2 text-sm ${window.innerWidth < 768 ? '' : "hidden"}`}>
                { filterToggle ? 
                <FontAwesomeIcon icon={faChevronDown}/> :
                <FontAwesomeIcon icon={faChevronLeft} />
              }
              </div>
            </div>
            <div className={`transition duration-200 ${filterToggle ? "" : "opacity-0 max-h-0 m-0"}`}>
              <div className={`max-md:ml-6 ${filterToggle ? "m-1" : ""}`}>
                <DatePicker selected={selectedDate} setSelected={setSelectedDate}/>
              </div>
              <div className="m-2 max-md:mb-0">
                <div className='flex w-100 mb-2'>
                  <SearchComponent 
                    className='mh-[5rem]'
                    searchClass='px-4 py-2 border rounded-md w-full' 
                    onSearchChange={handleSearch}
                    clear={clear}
                  >
                  </SearchComponent>
                </div>
                

                <div className="filters mt-2 flex flex-wrap">
                  <FiltersList listFilters={filtersList} onSelectedChange={x=>selectedListChanged(x)} clear={clear}/>
                </div>

                <div className="availability-filter border rounded-[6px] mt-2">
                  <div className='ml-2 mt-2'>{t.availability}:</div>
                  <div className={`flex mt-2 px-4 py-2 rounded-md w-full ${(!fromTime && toTime) ?  'border-red-500 justify-between' : ''}`}>
                    <div className='flex flex-row'>
                      <h2>{t.display.from}:</h2>
                      <input 
                        className='ml-2 availability-filter border rounded-md text-sm' 
                        type="time" 
                        onChange={handleFromChange} 
                        step='300'
                        value={fromTime ? fromTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
                      />
                    </div>

                    <div className='flex flex-row justify-end'>
                      {(!fromTime && toTime) && <label htmlFor="name"><FontAwesomeIcon icon={faWarning} /></label>}
                    </div>
                  </div>
                  <div className={`flex mt-2 px-4 py-2 rounded-md w-full ${(fromTime && !toTime) ?  'border-red-500 justify-between' : ''}`}>
                    <div className='flex flex-row'>
                      <h2>{t.display.till}:</h2>
                      <input 
                        className='ml-2 availability-filter border rounded-md text-sm' 
                        type="time" 
                        onChange={handleToChange} 
                        step='300'
                        value={toTime ? toTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
                      />
                    </div>

                    <div className='flex flex-row justify-end'>
                      {(fromTime && !toTime) && <label htmlFor="name"><FontAwesomeIcon icon={faWarning} /></label>}
                    </div>
                  </div>
                </div>

                <button className='btn px-4 py-2 mt-2 ml-0' onClick={clearFilters}>
                  {t.clear}
                </button>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="rooms-availability-box">
            <div className="main-header md:flex md:flex-row md:justify-between md:items-center ">
              {t.room_availability}
            <div className="text-xs italic opacity-50">
              {`All times are shown in your local time zone (${new Intl.DateTimeFormat(undefined, { timeZoneName: 'long' }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value})`}
            </div>
          </div>
            <div className=''>
              {rooms ? 
                rooms.length > 0 ?
                  (
                    <RoomList 
                      date={selectedDate}
                      rooms={rooms} 
                      onBookingClick={openBookingOverlay}
                      availabilityDate={availabilityDate}
                      onRoomClick={(room) => openRoomOverlay(room)}
                    />
                  ) : (
                    <div className='justify-center flex mt-10'>
                      <h1>{t.no_results}</h1>
                    </div>)
                : null
              }
            </div>
          </div>
        </div>
      </main>

      <BookingOverlay
        title={overlayBooking.editable ? "Confirm Your Booking" : overlayBooking.title}
        isOpen={showBookingOverlay} 
        onClose={closeBookingOverlay}
        room={overlayBooking.room}
        inputBooking={overlayBooking}
        editable={(isAuthorized(ACL.BOOKING) || isBookingOwner(overlayBooking) || overlayBooking.newBooking) && (new Date(overlayBooking.endTime) > new Date())}
        newBooking={overlayBooking.newBooking}/>
      
      {showRoomOverlay && (
        <RoomOverlay
          isOpen={showRoomOverlay}
          onClose={closeRoomOverlay}
          room={overlayRoom}
          createNew={false}
          editable={isAuthorized(ACL.ROOM)}
        />
      )}

      {isQuickBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            {bookingStatus === "loading" && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold mb-2">Processing Booking</h3>
                <p className="text-gray-600">{bookingMessage}</p>
              </>
            )}

            {bookingStatus === "success" && (
              <>
                <div className="text-green-500 text-4xl mb-2">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h3 className="text-lg font-semibold text-green-600 mb-2">Success!</h3>
                <p className="text-gray-600">{bookingMessage}</p>
              </>
            )}

            {bookingStatus === "error" && (
              <>
                <div className="text-red-500 text-4xl mb-2">
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
                <h3 className="text-lg font-semibold text-red-600 mb-2">Error</h3>
                <p className="text-gray-600">{bookingMessage}</p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Bookings;